import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';
import { useCallback, useMemo } from 'react';
import { useActiveFusionTarget } from '~/hooks/target/useActiveFusionTarget';
import { useActiveFusionWriter } from '~/hooks/target/useActiveFusionWriter';
import { useSessionOrNull } from '~/contexts/SessionContext';
import type { Image3D } from '~/domain/patient/image3d';
import { createRegistrationView } from '~/domain/projections/factories';
import {
    ImageRegistrationsView,
    RegistrationView,
} from '~/domain/projections/views';
import { useRegistrationListView } from '~/hooks/domain/useRegistrationListView';
import { RegistrationSelection } from '~/state/atoms/registration/registrationAtoms';

/**
 * Read state surfaced by {@link useRegistrationState}.
 *
 * Re-backed onto the new read seams: `imageRegistrations` comes from the
 * {@link useRegistrationListView} projection and the selection fields are
 * derived from the pair resolver. The field names and
 * shapes mirror the legacy `RegistrationStoreState` so the ~13 consumers do not
 * change. The view types ({@link ImageRegistrationsView} / {@link
 * RegistrationView}) are structural supersets of the old read-model view models
 * (they add a domain `id`), so consumers typed against the legacy shapes remain
 * compatible.
 */
export interface RegistrationListState {
    readonly imageRegistrations: ImageRegistrationsView[];
    readonly selectedSopInstanceUid?: string;
    readonly selectedImageSeriesUid?: string;
    readonly selectedRegistration?: RegistrationView;
}

/**
 * The registration selection of the primary row: the registration the pair
 * resolver reports the pair displayed through, and the image fused through
 * it.
 */
function useActiveRegistrationSelection(): RegistrationSelection | null {
    const fusion = useActiveFusionTarget();
    const displayed = fusion.displayedRegistration;
    return displayed
        ? {
              sopUid: displayed.sopInstanceUid,
              imageSeriesUid: fusion.secondaryImage?.seriesUid,
          }
        : null;
}

/**
 * Pure read-only view of the registrations for the current primary image plus
 * the current selection.
 *
 * Sources the registration list from the {@link useRegistrationListView}
 * projection (cached + referentially stable) and the selection from
 * {@link useActiveRegistrationSelection}. The selected registration view is
 * derived by resolving the live RigidRegistration from the session
 * patient and projecting it — so it always reflects the latest entity state.
 */
export function useRegistrationState(): RegistrationListState {
    const session = useSessionOrNull();
    // Same Recoil source the application-layer handler uses to push primary
    // image context — keeps the projection grouping aligned with the viewport.
    const primaryImage = useSelectedPrimaryImage();
    const imageRegistrations = useRegistrationListView(primaryImage ?? null);
    const selection = useActiveRegistrationSelection();

    const selectedSopInstanceUid = selection?.sopUid;
    const selectedImageSeriesUid = selection?.imageSeriesUid;

    const selectedRegistration = useMemo<RegistrationView | undefined>(() => {
        if (!selectedSopInstanceUid) {
            return undefined;
        }
        const reg = session?.patient.getRegistrationWithSopInstanceUid(
            selectedSopInstanceUid
        );
        return reg ? createRegistrationView(reg) : undefined;
        // `imageRegistrations` is an intentional extra dependency: it is the
        // referentially-stable projection that changes only when a domain
        // mutation bumps the store version. Re-running on its identity ensures
        // the derived selection view picks up field changes to the live
        // registration (e.g. rename/invert) even though `session` and
        // `selectedSopInstanceUid` are unchanged.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [session, selectedSopInstanceUid, imageRegistrations]);

    return useMemo<RegistrationListState>(
        () => ({
            imageRegistrations,
            selectedSopInstanceUid,
            selectedImageSeriesUid,
            selectedRegistration,
        }),
        [
            imageRegistrations,
            selectedSopInstanceUid,
            selectedImageSeriesUid,
            selectedRegistration,
        ]
    );
}

/**
 * Hook that returns a callback to activate a registration by its
 * SOP Instance UID and image series UID. Resolves domain objects
 * from the session patient and fuses the image onto the primary image
 * through that registration.
 *
 * The primary is `primary` when given, else the selected primary image as of
 * the last render. A caller that changes the primary image just before
 * activating passes the new one, which the render-time value does not yet
 * hold.
 */
export function useActivateRegistration(): (
    registrationSopUid: string,
    imageSeriesUid: string,
    primary?: Image3D
) => void {
    const session = useSessionOrNull();
    const primaryImage = useSelectedPrimaryImage();
    const { fuse } = useActiveFusionWriter();

    return useCallback(
        (
            registrationSopUid: string,
            imageSeriesUid: string,
            primary: Image3D | undefined = primaryImage
        ) => {
            const patient = session?.patient;
            const reg =
                patient?.getRegistrationWithSopInstanceUid(registrationSopUid);
            const img = patient?.images.find(
                (i) => i.seriesUid === imageSeriesUid
            );
            if (!reg || !img || !primary) {
                return;
            }
            fuse(primary, img, reg);
        },
        [session, primaryImage, fuse]
    );
}
